import * as React from 'react';
import { Card, Button, Form, InputGroup, Alert, ButtonGroup } from 'react-bootstrap';
import { FaLock, FaUserShield, FaUser } from 'react-icons/fa';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import services from '../../services';
import { ApiResponse } from 'services/api/base';

interface State {
    email: string;
    password: string;
    loginType: 'standard' | 'admin';
    isLoading: boolean;
    error: string | null;
    showPassword: boolean; 
}

class Login extends React.PureComponent<{}, State> {
    public state: State = {
        email: '',
        password: '',
        loginType: 'standard',
        isLoading: false,
        error: null,
        showPassword: false, 
    };

    private handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        this.setState({ isLoading: true, error: null });

        try {
            // Pivot to the full URL based on loginType
            // Using the environment variables you provided
            const loginUrl = this.state.loginType === 'admin' 
                ? process.env.REACT_APP_ADMIN_LOGIN_PATH 
                : process.env.REACT_APP_LOGIN_PATH;

            if (!loginUrl) {
                throw new Error("Login configuration is missing.");
            }

            // 1. Initial Authentication against Connect
            // We pass the absolute URL here. 
            // Ensure your 'directLogin' service is prepared to handle an absolute URL.
            const response: ApiResponse = await services.api.user.directLogin(
                loginUrl, 
                this.state.email, 
                this.state.password
            );

            // ... rest of your existing logic (SyncSession, etc.) ...
            if (!response.isSuccess) {
                const msg = response.data?.message || response.message || 'Login declined.';
                localStorage.setItem('userInfo', '');
                localStorage.setItem('token', '');
                this.setState({ error: msg, isLoading: false });
                return;
            }

            const tokenStr = response.data.token;
            if (!tokenStr) {
                this.setState({ error: "No token provided by authentication service.", isLoading: false });
                return;
            }

            localStorage.setItem('token', tokenStr);

            const syncResponse: ApiResponse = await services.api.user.syncSession(tokenStr);

            if (syncResponse.isSuccess) {
                const enrichedUserData = syncResponse.data.user || syncResponse.data;
                localStorage.setItem('userInfo', JSON.stringify(enrichedUserData));
                window.location.href = "/";
            } else {
                const errorMsg = syncResponse.data?.message || syncResponse.message || "Account validation failed.";
                localStorage.removeItem('token');
                localStorage.removeItem('userInfo');
                this.setState({ error: errorMsg, isLoading: false });
            }

        } catch (err) {
            console.log(err); 
            this.setState({ 
                error: "Connection error. Is the API running?", 
                isLoading: false 
            });
        }
    };

    public render() {
        const isAdmin = this.state.loginType === 'admin';

        return (
            <div className="d-flex justify-content-center pt-5" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
                <Card className="shadow border-0 mt-5" style={{ width: '100%', maxWidth: '420px', borderRadius: '15px', height: 'fit-content' }}>
                    <Card.Body className="p-5">
                        <div className="text-center mb-4">
                            <div className="d-inline-flex align-items-center justify-content-center mb-3" 
                                 style={{ 
                                    width: '70px', 
                                    height: '70px', 
                                    backgroundColor: isAdmin ? '#fff4e5' : '#e7f0ff', 
                                    borderRadius: '50%',
                                    transition: '0.3s all' 
                                 }}>
                                {isAdmin ? <FaUserShield size={30} color="#fd7e14" /> : <FaLock size={28} color="#0d6efd" />}
                            </div>
                            <h3 className="fw-bold mb-1">Compass Connect</h3>
                            <p className="text-muted small">Select your portal and sign in</p>
                        </div>

                        <div className="mb-4 text-center">
                            <ButtonGroup className="w-100 shadow-sm" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                                <Button 
                                    variant={!isAdmin ? "primary" : "outline-secondary"}
                                    className="py-2 fw-bold"
                                    onClick={() => this.setState({ loginType: 'standard' })}
                                    style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}
                                >
                                    <FaUser className="me-2" size={12} /> Standard
                                </Button>
                                <Button 
                                    variant={isAdmin ? "warning" : "outline-secondary"}
                                    className="py-2 fw-bold"
                                    onClick={() => this.setState({ loginType: 'admin' })}
                                    style={{ borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }}
                                >
                                    <FaUserShield className="me-2" size={14} /> Admin
                                </Button>
                            </ButtonGroup>
                        </div>

                        {this.state.error && <Alert variant="danger" className="small">{this.state.error}</Alert>}

                        <Form onSubmit={this.handleLogin}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-bold text-uppercase text-muted" style={{ fontSize: '10px' }}>Email Address</Form.Label>
                                <Form.Control 
                                    type="email" 
                                    placeholder="name@company.com"
                                    className="py-2"
                                    value={this.state.email}
                                    onChange={(e) => this.setState({ email: e.target.value })}
                                    required 
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="small fw-bold text-uppercase text-muted" style={{ fontSize: '10px' }}>
                                    Password
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control 
                                        type={this.state.showPassword ? "text" : "password"} 
                                        placeholder="Enter your password"
                                        className="py-2"
                                        value={this.state.password}
                                        onChange={(e) => this.setState({ password: e.target.value })}
                                        required 
                                        style={{ borderRight: 'none' }} // Manually remove the middle border
                                    />
                                    <InputGroup.Text 
                                        className="bg-white py-2 px-3" 
                                        style={{ 
                                            cursor: 'pointer', 
                                            borderLeft: 'none' // Remove the middle border from the icon side
                                        }}
                                        onClick={() => this.setState({ showPassword: !this.state.showPassword })}
                                    >
                                        {this.state.showPassword ? (
                                            <FaEyeSlash className="text-muted" />
                                        ) : (
                                            <FaEye className="text-muted" />
                                        )}
                                    </InputGroup.Text>
                                </InputGroup>
                            </Form.Group>

                            <Button 
                                variant={isAdmin ? "warning" : "primary"}
                                size="lg" 
                                type="submit"
                                className="w-100 py-3 fw-bold shadow-sm"
                                disabled={this.state.isLoading}
                                style={{ borderRadius: '10px' }}
                            >
                                {this.state.isLoading ? 'Processing...' : `Login as ${isAdmin ? 'Admin' : 'User'}`}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </div>
        );
    }
}

export default Login;